<template>
  <div :class="{ 'opacity-40': isLoginSuccess }">
    <BackButton v-if="backButton" @back="emit('back')" />

    <h1 class="theme-1 mb-5 text-2xl font-semibold leading-12">{{ t("pages.auth.one-time-password.title") }}</h1>

    <div class="mb-2 text-sm">
      <div>
        {{ t("pages.auth.one-time-password.description-code-sent") }}
      </div>

      <div class="font-semibold">
        {{ email }}
      </div>

      <div>
        {{ t("pages.auth.one-time-password.description-enter-code") }}
      </div>
    </div>

    <IAIFadeAnimation :show="loading" class="mb-2 flex">
      <div class="flex flex-col items-center justify-center">
        <IAISpinner class="mr-2" />
      </div>

      <div class="flex flex-col items-center justify-center">
        <span class="text-sm"> {{ t("pages.auth.one-time-password.loading") }} </span>
      </div>
    </IAIFadeAnimation>

    <div class="relative mb-2">
      <OTPInput
        v-model="onetime_password_code"
        style="max-width: 21.25rem"
        input-classes="otp-input"
        :num-inputs="6"
        input-type="letter-numeric"
        :should-auto-focus="true"
        :filter-paste-strings="['-']"
        upper-case
        :seperator-every-n="3"
        @onComplete="onSubmit"
      />

      <span
        v-if="isLoginSuccess"
        class="login-success-loader absolute left-0 top-0 -ml-1 mt-0.5 flex w-full justify-center"
      >
        <IAISpinner :size="3" />
      </span>
    </div>

    <IAIFadeAnimation :show="!!loginError" class="msg mb-4 flex gap-1 text-theme-error">
      <div>
        {{ loginError || "&nbsp;" }}
      </div>
    </IAIFadeAnimation>

    <span v-if="resendExceeded" class="text-sm">
      <div class="mb-7">{{ t("pages.auth.one-time-password.resend-exceeded.title") }}</div>

      <span class="mr-2">{{ t("pages.auth.one-time-password.resend-exceeded.description") }}</span>

      <IAILink
        :text="t('pages.auth.one-time-password.resend-exceeded.contact-us')"
        url="https://support.imagen-ai.com/hc/en-us/requests/new?ticket_form_id=23280822564381"
      />
    </span>

    <span v-else class="text-sm">
      <div>{{ t("pages.auth.one-time-password.description-cant-find-code") }}</div>

      <div class="mb-2">
        <span class="mr-1">
          {{ t("pages.auth.one-time-password.description-check-spam") }}
        </span>

        <IAIButton
          variant="link-underlined"
          :disabled="resendTimeout"
          :label="t('pages.auth.one-time-password.description-resend-code')"
          @click="resendCode"
        />
      </div>

      <IAIFadeAnimation :show="codeResentNotification" class="msg mb-7 flex gap-1 text-theme-success-c">
        <img :src="GreenCheck.src" /> {{ t("pages.auth.one-time-password.resend_sent") }}
      </IAIFadeAnimation>

      <div v-if="resendCodeError" class="text-theme-error">
        <span> {{ t("pages.auth.sign-up.general-error-prefix") }} </span>

        <IAILink
          class="ml-2 mr-2 text-theme-error"
          :text="t('pages.auth.sign-up.contact-support')"
          url="https://support.imagen-ai.com/hc/en-us/requests/new?ticket_form_id=23280822564381"
        />

        <span>{{ t("pages.auth.sign-up.general-error-suffix") }} </span>
      </div>

      <div v-else>
        <span class="mr-1">{{ t("pages.auth.one-time-password.description-cant-sign-in") }} </span>

        <IAILink
          :text="t('pages.auth.one-time-password.contact-us')"
          url="https://support.imagen-ai.com/hc/en-us/requests/new?ticket_form_id=23280822564381"
        />
      </div>
    </span>
  </div>
</template>

<script setup lang="ts">
import { t } from "@/locales/vue";
import { IAIButton, IAIFadeAnimation, OTPInput, IAILink, IAISpinner } from "@/core-ui-vue";
import { ref } from "vue";
import { onetimePasswordLogin, sendOTP } from "../services/api";
import GreenCheck from "../assets/GreenCheck.svg";
import BackButton from "./BackButton.vue";

const RESEND_CODE_INTERVAL_IN_MS = 1000 * 10;

const props = withDefaults(
  defineProps<{
    email: string;
    backButton: boolean;
  }>(),
  {
    backButton: true,
  }
);

const isLoginSuccess = ref(false);

const emit = defineEmits<{
  (event: "loginSuccess"): void;
  (event: "back"): void;
}>();

const onetime_password_code = ref("");
const loading = ref(false);
const resendCodeCounter = ref(0);
const resendTimeout = ref(false);
const resendExceeded = ref(false);
const codeResentNotification = ref(false);
const loginError = ref("");
const resendCodeError = ref(false);

const clearInput = () => {
  onetime_password_code.value = "";
};

const onSubmit = async (code: string) => {
  loading.value = true;
  codeResentNotification.value = false;
  loginError.value = "";
  const loginResponse = await onetimePasswordLogin(props.email, code);
  loading.value = false;
  if (loginResponse.status === "FAIL") {
    if (loginResponse.errorType === "INVALID_CODE") {
      loginError.value = t("pages.auth.one-time-password.errors.invalid-code");
    } else if (loginResponse.errorType === "CODE_EXPIRED_AND_RESEND") {
      loginError.value = t("pages.auth.one-time-password.errors.code-expired-and-resend");
    } else if (loginResponse.errorType === "ACCOUNT_INACTIVE") {
      loginError.value = t("pages.auth.one-time-password.errors.account-inactive");
    } else if (loginResponse.errorType === "ACCOUNT_NOT_FOUND") {
      loginError.value = t("pages.auth.one-time-password.errors.account-not-found");
    } else {
      loginError.value = t("pages.auth.one-time-password.errors.invalid-code");
    }

    clearInput();
    loading.value = false;
    return;
  }

  emit("loginSuccess");
  isLoginSuccess.value = true;
};

const resendCode = async () => {
  codeResentNotification.value = false;
  if (resendCodeCounter.value > 2) {
    resendExceeded.value = true;
    return;
  }

  try {
    resendTimeout.value = true;
    await sendOTP(props.email);
    resendCodeCounter.value = resendCodeCounter.value + 1;
    codeResentNotification.value = true;
  } catch (err) {
    resendCodeError.value = false;
    // @@@ good point for logging
  } finally {
    setTimeout(() => {
      resendTimeout.value = false;
    }, RESEND_CODE_INTERVAL_IN_MS);
  }
};
</script>
